<template>
	<div class="bnt-toolbar" :class="{ 'bnt-toolbar--inline': inlineFilters }">
		<div class="bnt-toolbar__top">
			<div class="bnt-toolbar__left">
				<v-text-field
					:value="value"
					class="bnt-search"
					prepend-inner-icon="mdi-magnify"
					:placeholder="placeholder"
					hide-details
					clearable
					solo
					flat
					dense
					@input="$emit('input', $event)"
				/>

				<v-tooltip v-if="!inlineFilters" bottom z-index="200">
					<template #activator="{ on, attrs }">
						<v-badge
							:value="!!appliedFilters"
							:content="appliedFilters"
							color="error"
							overlap
							offset-x="10"
							offset-y="10"
						>
							<v-btn
								v-bind="attrs"
								v-on="on"
								class="bnt-icon-btn"
								:class="{ 'bnt-icon-btn--active': filtersOpen }"
								color="primary"
								fab
								depressed
								@click="filtersOpen = !filtersOpen"
							>
								<v-icon>mdi-filter-variant</v-icon>
							</v-btn>
						</v-badge>
					</template>
					<span>{{ filtersOpen ? 'Hide filters' : 'Show filters' }}</span>
				</v-tooltip>

				<div
					v-if="inlineFilters"
					class="bnt-toolbar__filters bnt-toolbar__filters--inline"
				>
					<slot />
				</div>
			</div>

			<div class="bnt-toolbar__right">
				<slot name="actions">
					<v-menu
						v-if="showExcel"
						offset-y
						left
						:nudge-bottom="4"
						content-class="bnt-excel-menu"
					>
						<template #activator="{ on, attrs }">
							<v-btn
								v-bind="attrs"
								v-on="on"
								class="bnt-excel-btn text-none"
								depressed
							>
								<img :src="excelIcon" class="bnt-excel-btn__icon" alt="" />
								<span>Export To Excel</span>
								<v-icon small class="ml-1">mdi-chevron-down</v-icon>
							</v-btn>
						</template>
						<v-list dense>
							<v-list-item @click="$emit('excel', 'billables')">
								<v-list-item-title>Download Billables</v-list-item-title>
							</v-list-item>
							<v-list-item @click="$emit('excel', 'billableItems')">
								<v-list-item-title>Download Billable Items</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>

					<v-btn
						v-if="showSuggested"
						class="bnt-primary-btn text-none"
						color="primary"
						depressed
						:to="suggestedTo"
					>
						<v-icon left small>mdi-plus</v-icon>
						Billable Items
					</v-btn>
				</slot>
				<slot name="actions-extra" />
			</div>
		</div>

		<div
			v-if="!inlineFilters"
			v-show="filtersOpen"
			class="bnt-toolbar__filters"
		>
			<slot />
		</div>
		<div
			v-if="$slots['filters-secondary'] || $scopedSlots['filters-secondary']"
			v-show="inlineFilters || filtersOpen"
			class="bnt-toolbar__filters bnt-toolbar__filters--secondary"
		>
			<slot name="filters-secondary" />
		</div>
	</div>
</template>

<script>
import excelIcon from '@/assets/excel-icon.svg'

export default {
	name: 'BillablesNestToolbar',
	props: {
		value: { type: String, default: '' },
		placeholder: { type: String, default: 'Search for billables' },
		appliedFilters: { type: Number, default: 0 },
		showExcel: { type: Boolean, default: true },
		showSuggested: { type: Boolean, default: true },
		suggestedTo: { type: [String, Object], default: '/admin/suggestedBillables' },
		filtersOpenByDefault: { type: Boolean, default: false },
		inlineFilters: { type: Boolean, default: false },
	},
	data() {
		return {
			excelIcon,
			filtersOpen: this.filtersOpenByDefault || this.inlineFilters,
		}
	},
}
</script>

<style lang="scss" scoped>
.bnt-toolbar {
	margin-bottom: 16px;
	width: 100%;
}

.bnt-toolbar__top {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	flex-wrap: nowrap;
}

.bnt-toolbar__left {
	display: flex;
	align-items: center;
	gap: 10px;
	flex: 1 1 auto;
	min-width: 0;
	flex-wrap: nowrap;

	/* Keep tooltip/badge wrappers from shifting icon buttons off the search baseline */
	::v-deep > .v-tooltip,
	::v-deep > span,
	::v-deep .v-badge {
		display: inline-flex;
		align-items: center;
		vertical-align: middle;
	}

	::v-deep .v-badge__wrapper {
		display: inline-flex;
		align-items: center;
	}
}

.bnt-toolbar__right {
	display: flex;
	align-items: center;
	gap: 10px;
	flex: 0 0 auto;
	flex-wrap: nowrap;
}

.bnt-search {
	flex: 1 1 auto;
	min-width: 180px;
	max-width: 420px;
	width: auto;
	margin: 0;
	padding: 0;

	::v-deep.v-input,
	::v-deep .v-input {
		margin-top: 0 !important;
		padding-top: 0 !important;
	}

	::v-deep .v-input__control {
		min-height: 40px !important;
	}

	::v-deep .v-input__slot {
		background: #fff !important;
		border: 1px solid #d7dbe0 !important;
		border-radius: 10px !important;
		box-shadow: none !important;
		min-height: 40px !important;
		height: 40px !important;
		padding: 0 14px !important;
		margin: 0 !important;
		display: flex !important;
		align-items: center !important;
	}

	::v-deep .v-text-field__slot {
		display: flex;
		align-items: center;
		height: 100%;
		margin: 0;
	}

	::v-deep .v-input__prepend-inner,
	::v-deep .v-input__append-inner {
		margin-top: 0 !important;
		margin-bottom: 0 !important;
		align-self: center !important;
		padding: 0 !important;
		height: 24px;
		display: flex;
		align-items: center;
	}

	::v-deep .v-input__prepend-inner {
		margin-right: 8px !important;
	}

	::v-deep input {
		font-size: 0.9rem;
		line-height: 1.25;
		color: var(--v-newDesignText-base);
		margin: 0 !important;
		padding: 0 !important;
		height: 24px !important;
		max-height: 24px !important;
	}

	::v-deep input::placeholder {
		color: #9aa3af;
		opacity: 1;
	}

	::v-deep .v-icon {
		color: #9aa3af !important;
		font-size: 20px !important;
	}

	::v-deep .v-input__icon {
		height: 24px;
		min-width: 24px;
		width: 24px;
	}

	::v-deep .v-input__icon--clear .v-icon {
		font-size: 18px !important;
	}
}

.bnt-toolbar--inline {
	.bnt-search {
		flex: 0 0 300px;
		min-width: 240px;
		max-width: 300px;
	}

	.bnt-toolbar__left {
		gap: 8px;
		width: 100%;
	}

	.bnt-toolbar__filters--inline {
		flex: 0 0 auto !important;
		width: auto !important;
		max-width: none;
		justify-content: flex-end;
		gap: 8px;
		min-width: 0;
		margin-left: auto;

		/* Keep same size as when Month was in this row (5 equal slots) */
		> * {
			flex: 0 0 210px !important;
			width: 210px !important;
			min-width: 210px !important;
			max-width: 210px !important;
		}
	}
}

.bnt-icon-btn {
	width: 40px !important;
	height: 40px !important;
	min-width: 40px !important;
	flex: 0 0 40px;
	box-shadow: none !important;
	margin: 0 !important;

	&--active {
		filter: brightness(0.92);
	}
}

.bnt-excel-btn {
	background: #21a366 !important;
	color: #fff !important;
	border-radius: 10px !important;
	height: 40px !important;
	padding: 0 14px !important;
	font-weight: 600;
	letter-spacing: 0;
	box-shadow: none !important;
	white-space: nowrap;

	&__icon {
		width: 16px;
		height: 16px;
		margin-right: 8px;
		filter: invert(99%) sepia(7%) saturate(35%) hue-rotate(255deg) brightness(110%) contrast(99%);
	}
}

.bnt-primary-btn {
	border-radius: 10px !important;
	height: 40px !important;
	padding: 0 16px !important;
	font-weight: 600;
	letter-spacing: 0;
	box-shadow: none !important;
	white-space: nowrap;
}

.bnt-toolbar__filters {
	display: grid;
	grid-template-columns: repeat(6, minmax(0, 1fr));
	gap: 10px;
	margin-top: 12px;
	width: 100%;
	overflow: visible;
	position: relative;
	z-index: 1;

	&--secondary {
		margin-top: 10px;
	}

	&--inline {
		display: flex;
		flex: 0 0 auto;
		width: auto;
		min-width: 0;
		margin-top: 0;
		margin-left: auto;
		gap: 8px;
		grid-template-columns: none;

		> * {
			flex: 0 0 210px;
			width: 210px;
			min-width: 210px;
			max-width: 210px;
		}

		::v-deep > .v-input,
		::v-deep > .v-text-field,
		::v-deep > .v-select,
		::v-deep > .v-autocomplete,
		::v-deep > .q-date-picker,
		::v-deep > * {
			width: 210px !important;
			max-width: 210px !important;
			min-width: 210px !important;
			flex: 0 0 210px !important;
		}
	}

	/* Nest-only field look — does not affect global FilterSearchInput */
	::v-deep > .v-input,
	::v-deep > .v-text-field,
	::v-deep > .v-select,
	::v-deep > .v-autocomplete,
	::v-deep > .q-date-picker,
	::v-deep > * {
		min-width: 0;
		width: 100%;
	}

	::v-deep .v-input__slot {
		background: #fff !important;
		border: 1px solid #d7dbe0 !important;
		border-radius: 10px !important;
		box-shadow: none !important;
		min-height: 40px !important;
		height: 40px !important;
		display: flex !important;
		align-items: center !important;
		padding-top: 0 !important;
		padding-bottom: 0 !important;
	}

	::v-deep .v-input__control {
		min-height: 40px !important;
	}

	::v-deep .v-input--is-label-active .v-label,
	::v-deep .v-input--is-dirty .v-label,
	::v-deep .v-input--is-focused .v-label {
		/* Keep floating label from pushing content off-center */
		transform: translateY(-22px) scale(0.75) !important;
	}

	::v-deep .v-select__slot,
	::v-deep .v-text-field__slot {
		height: 40px;
		display: flex;
		align-items: center;
		margin: 0;
	}

	::v-deep .v-input__append-inner,
	::v-deep .v-input__prepend-inner {
		margin-top: 0 !important;
		align-self: center !important;
		padding: 0 !important;
	}

	::v-deep .v-select__selections {
		flex-wrap: nowrap !important;
		overflow: hidden;
		max-width: 100%;
		padding: 0 !important;
		min-height: 0 !important;
		height: 24px;
		align-items: center;
		display: flex;
	}

	::v-deep .v-select__selection--comma,
	::v-deep .bnt-select-summary {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		max-width: 100%;
		margin: 0 !important;
		line-height: 1.25;
		display: inline-flex;
		align-items: center;
	}

	::v-deep .v-select__selection {
		margin: 0 !important;
	}

	::v-deep input {
		margin: 0 !important;
		padding: 0 !important;
		max-height: 24px;
	}

	::v-deep .v-text-field--outlined fieldset {
		border: none !important;
	}

	::v-deep .v-text-field.v-text-field--enclosed .v-text-field__details,
	::v-deep .v-input__details {
		display: none;
	}

	::v-deep .v-label {
		font-size: 0.85rem;
		color: #8b93a0 !important;
	}

	::v-deep .v-select__selections,
	::v-deep input {
		font-size: 0.85rem;
	}
}

@media (max-width: 1100px) {
	.bnt-toolbar__filters:not(.bnt-toolbar__filters--inline) {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}

	.bnt-toolbar--inline {
		.bnt-toolbar__top {
			flex-wrap: wrap;
		}

		.bnt-toolbar__left {
			flex-wrap: wrap;
			width: 100%;
		}

		.bnt-search {
			flex: 0 1 280px;
			min-width: 200px;
			max-width: 300px;
		}

		.bnt-toolbar__filters--inline {
			flex: 0 1 auto;
			flex-wrap: wrap;
			justify-content: flex-end;
			margin-left: auto;

			> * {
				flex: 0 0 180px;
				width: 180px;
				min-width: 180px;
				max-width: 180px;
			}
		}
	}
}

@media (max-width: 700px) {
	.bnt-toolbar__top {
		flex-wrap: wrap;
	}

	.bnt-toolbar__right {
		width: 100%;
		justify-content: flex-end;
		flex-wrap: wrap;
	}

	.bnt-toolbar__filters:not(.bnt-toolbar__filters--inline) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.bnt-toolbar--inline .bnt-toolbar__filters--inline > * {
		flex: 0 0 160px;
		width: 160px;
		min-width: 160px;
		max-width: 160px;
	}
}
</style>
